import { adaptV2ToProductPickerView } from "../../Interview/productPickerV2View"

const asList = (value) => (Array.isArray(value) ? value.filter(Boolean) : [])

const fullName = (person) => [person?.firstname, person?.lastname].filter(Boolean).join(" ").trim()

const addressKey = (address) => `${address || ""}`.trim().toLowerCase()

const hasOccupationData = (occupation) => {
    if (!occupation || typeof occupation !== "object") return false
    if (occupation.selfemployed || `${occupation.employername || ""}`.trim()) return true
    return Object.values(occupation.income || {}).some((value) => parseFloat(value) > 0)
}

/**
 * The DTI calculator and getDTIResult only understand a primary borrower and a
 * single co-borrower. On multi-borrower (V2) loans, adaptV2ToProductPickerView
 * puts participants 3..N in `coborrowers`, which both ignore — so their income,
 * rent, owned properties and alimony silently dropped out of DTI.
 *
 * Until the calculator becomes per-participant, fold every co-borrower into the
 * single co-borrower slot so the loan-level totals cover all borrowers:
 * - extra participants' jobs become additional occupations,
 * - other income and alimony are concatenated,
 * - owned properties are merged, de-duplicated by address (a property owned
 *   jointly by two co-borrowers is listed under each owner),
 * - rent is summed across co-borrowers who rent somewhere other than the
 *   primary borrower's address (once per address).
 * Liabilities are already collected across all participants by the adapter.
 */
export const foldCoborrowersForDTI = (view) => {
    const extras = asList(view?.coborrowers)
    if (!extras.length || view.hascoborrower !== "withcoborrower" || !view.coborrower) return view

    const people = [view.coborrower, ...extras]
    const [first] = people

    const seenProperties = new Set()
    const ownedproperties = []
    people.forEach((person) => {
        asList(person.ownedproperties).forEach((property) => {
            const key = addressKey(property.address)
            if (key && seenProperties.has(key)) return
            if (key) seenProperties.add(key)
            ownedproperties.push(property)
        })
    })

    // Mirror the calculator's rule for the single co-borrower: rent counts only
    // when they live somewhere other than the primary borrower.
    const borrowerStreet = view.borrower?.presentaddressstreet
    const seenRentals = new Set()
    const renters = people.filter((person) => {
        if (person.presentaddressownership !== "rent") return false
        if (person.presentaddressstreet === borrowerStreet) return false
        const key = addressKey(person.presentaddressstreet)
        if (key && seenRentals.has(key)) return false
        if (key) seenRentals.add(key)
        return true
    })
    const residence = renters.length
        ? {
            presentaddressownership: "rent",
            presentaddressstreet: renters[0].presentaddressstreet,
            expenses: {
                ...(first.expenses || {}),
                currentrent: renters.reduce((sum, person) => sum + (parseFloat(person.expenses?.currentrent) || 0), 0),
            },
        }
        : {}

    const extraOccupations = extras.flatMap((person) => [
        ...(hasOccupationData(person.occupation) ? [person.occupation] : []),
        ...asList(person.otheroccupations),
    ])

    const coborrower = {
        ...first,
        // Labels the calculator's co-borrower tab and cards, e.g. "Jane Doe +2".
        lastname: [first.lastname, `+${extras.length}`].filter(Boolean).join(" "),
        foldedNames: people.map(fullName).filter(Boolean),
        otheroccupations: [...asList(first.otheroccupations), ...extraOccupations],
        income: {
            ...(first.income || {}),
            otherincome: people.flatMap((person) => asList(person.income?.otherincome)),
        },
        finances: {
            ...(first.finances || {}),
            alimony: people.flatMap((person) => asList(person.finances?.alimony)),
        },
        ownedproperties,
        ...residence,
    }

    return { ...view, coborrower, coborrowers: [] }
}

// Same identity collectLegacyLiabilities de-duplicates by.
const loanIdentity = (loan) => loan?.key
    || loan?.accountnumber
    || `${loan?.name || ""}:${loan?.monthlypayment || ""}:${loan?.remainingbalance || ""}`
const accountAndName = (loan) => {
    const account = `${loan?.accountnumber || ""}`.trim()
    return account ? `${account}|${`${loan?.name || ""}`.trim().toLowerCase()}` : ""
}

/**
 * A mortgage on an owned property belongs to that property: the calculator
 * counts it there (P&I in "Property you own", skipped when the property is
 * sold / pending). collectLegacyLiabilities also lists every REO mortgage in
 * the flat debt list, so the same loan was counted again as a plain debt —
 * including loans on properties already sold.
 */
export const dropPropertyLinkedLoans = (view) => {
    const loans = asList(view?.assetsandliabilities?.loans)
    if (!loans.length) return view
    const people = [view.borrower, view.coborrower, ...asList(view.coborrowers)].filter(Boolean)
    const identities = new Set()
    const accounts = new Set()
    people.forEach((person) => asList(person.ownedproperties).forEach((property) => {
        asList(property.loans).forEach((loan) => {
            identities.add(loanIdentity(loan))
            const account = accountAndName(loan)
            if (account) accounts.add(account)
        })
    }))
    if (!identities.size) return view
    const kept = loans.filter((loan) => !identities.has(loanIdentity(loan)) && !accounts.has(accountAndName(loan)))
    if (kept.length === loans.length) return view
    return { ...view, assetsandliabilities: { ...view.assetsandliabilities, loans: kept } }
}

/** DTI view of a V2 loan: property mortgages counted once, all co-borrowers folded in. */
export const prepareDTIView = (view) => foldCoborrowersForDTI(dropPropertyLinkedLoans(view))

/**
 * Legacy-shaped application for the DTI calculator on V2 loan pages, built from
 * the split-table V2 application (fresh after every 1003 save) instead of the
 * Redux copy loaded from loanstates, which V2 pages no longer write back.
 */
export const buildDTIApplicationV2 = (v2Application, dtiState = {}) => {
    const view = prepareDTIView(adaptV2ToProductPickerView(v2Application))
    const property = view.property || {}
    return {
        ...view,
        property: {
            ...property,
            purchase: property.purchase || {},
            mobilehomeinfo: property.mobilehomeinfo || {},
        },
        dtiCalculation: dtiState.dtiCalculation || {},
        currentDTIResult: dtiState.currentDTIResult || {},
    }
}
